import { CbhIcon, PageFooter, Text, useModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useCallback } from "react";

import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { useSendWorkBadgeEmailWithPdf } from "../api/useSendWorkBadgeEmailWithPdf";
import { WorkBadgeEmailFailureBottomSheet } from "./WorkBadgeEmailFailureBottomSheet";
import { WorkBadgeEmailSuccessBottomSheet } from "./WorkBadgeEmailSuccessBottomSheet";
import { WorkBadgePreviewContainer } from "./WorkBadgePreviewContainer";

interface WorkBadgesReadyProps {
  qualificationName: string;
  onBack: () => void;
  emailSuccessModal: {
    primaryButtonText: string;
    onPrimaryButtonClick: () => void;
  };
  workerHeadshotBase64: string;
}

export function WorkBadgesReady(props: WorkBadgesReadyProps) {
  const { qualificationName, onBack, emailSuccessModal, workerHeadshotBase64 } = props;

  const emailSuccessModalState = useModalState();
  const emailFailureModalState = useModalState();

  const { mutateAsync: sendWorkBadgeEmailWithPdf, isLoading: isSendingBadgeEmail } =
    useSendWorkBadgeEmailWithPdf();

  const handleSendEmail = useCallback(async () => {
    try {
      await sendWorkBadgeEmailWithPdf({
        qualificationName,
        workerHeadshotBase64,
      });

      emailSuccessModalState.openModal();
    } catch {
      emailFailureModalState.openModal();
    }
  }, [
    sendWorkBadgeEmailWithPdf,
    qualificationName,
    workerHeadshotBase64,
    emailSuccessModalState,
    emailFailureModalState,
  ]);

  const handleRetry = useCallback(() => {
    emailFailureModalState.closeModal();
    void handleSendEmail();
  }, [emailFailureModalState, handleSendEmail]);

  return (
    <>
      <StickyScrollPageHeader
        title="Your Badge is Ready!"
        headerVariant="tertiary"
        onClickBack={onBack}
      />
      <PageContent>
        <Stack spacing={5}>
          <WorkBadgePreviewContainer
            qualificationName={qualificationName}
            workerHeadshotBase64={workerHeadshotBase64}
          />

          <Text bold variant="h6">
            Your badge looks great. Now let&apos;s get your hands on a physical version you can
            bring with you to your shifts!
          </Text>
        </Stack>
      </PageContent>

      <PageFooter>
        <Stack width="100%" spacing={5}>
          {/* TODO: Replace this with filled warning icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <CbhIcon type="triangle-exclamation" />
            <Text variant="body1">
              Many workplaces require a badge to be worn while working. Have your badge with you at
              all times so you are always prepared.
            </Text>
          </Stack>
          <LoadingButton
            fullWidth
            size="large"
            isLoading={isSendingBadgeEmail}
            onClick={handleSendEmail}
          >
            Email a badge print-out
          </LoadingButton>
        </Stack>
      </PageFooter>

      <WorkBadgeEmailSuccessBottomSheet
        modalState={emailSuccessModalState}
        primaryButtonText={emailSuccessModal.primaryButtonText}
        onPrimaryButtonClick={emailSuccessModal.onPrimaryButtonClick}
      />

      <WorkBadgeEmailFailureBottomSheet modalState={emailFailureModalState} onRetry={handleRetry} />
    </>
  );
}
